import type { RouterOutputs } from '~/trpc-extra/api';
import { downloadFile } from './download';

type TestSuiteExportData = RouterOutputs['testSuite']['getCsvExportData'];

// Escapes a value for CSV format (handles commas, quotes, and newlines)
function escapeCsvValue(value: string): string {
  // If the value contains commas, quotes, or newlines, wrap it in quotes and escape internal quotes
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// Formats steps and operations into a hierarchical string for the Actions column
function formatActionsColumn(steps: TestSuiteExportData['tests'][number]['steps']): string {
  return steps
    .map((step) => {
      const stepLine = step.description ? `Step: ${step.description}` : `Step ${step.stepOrder + 1}`;
      const operationLines = step.operations.map((op) => `  - ${op.description}`).join('\n');
      return operationLines ? `${stepLine}\n${operationLines}` : stepLine;
    })
    .join('\n\n');
}

// Generates CSV content from test suite export data
export function generateTestSuiteCsv(data: TestSuiteExportData): string {
  const headers = ['Test Name', 'Tags', 'Actions', 'Skipped'];
  const headerRow = headers.map(escapeCsvValue).join(',');

  const dataRows = data.tests.map((test) => {
    const testName = escapeCsvValue(test.name);
    const tags = escapeCsvValue(test.tags.join(', '));
    const actions = escapeCsvValue(formatActionsColumn(test.steps));
    const skipped = test.skip ? 'Yes' : 'No';

    return [testName, tags, actions, skipped].join(',');
  });

  return [headerRow, ...dataRows].join('\n');
}

// Downloads test suite data as a CSV file
export function downloadTestSuiteCsv(data: TestSuiteExportData) {
  const csvContent = generateTestSuiteCsv(data);
  const sanitizedSuiteName = data.name.replace(/[^a-zA-Z0-9-_]/g, '_');
  const filename = `${sanitizedSuiteName}_test_suite.csv`;

  downloadFile(csvContent, {
    filename,
    type: 'text/csv;charset=utf-8;',
  });
}
